import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const faqs: FAQItem[] = [
    {
      id: 'amenities',
      question: 'What amenities are available at Purva Swanlake?',
      answer: 'Purva Swanlake offers 40+ amenities including swimming pool, wave pool, gymnasium, clubhouse, sports courts (tennis, squash, cricket, basketball), badminton court, football court, maze garden, jogging track, children\'s play area, and landscaped gardens. All amenities are well-maintained and available 24/7 for residents.',
    },
    {
      id: 'security',
      question: 'What security measures are in place?',
      answer: 'We provide 24/7 security with CCTV surveillance throughout the complex, smart card access control, video door phones at all apartments, trained security personnel at entry and exit points, and regular security patrols. All residents have access to a secure community with professional security management.',
    },
    {
      id: 'maintenance',
      question: 'How is maintenance handled?',
      answer: 'Maintenance is managed by GoIntegra Facility Management Services, our professional facility management agency. They handle daily maintenance, housekeeping, preventive maintenance programs, landscaping, waste management, and all common area upkeep. Residents can report issues through the management office.',
    },
    {
      id: 'water',
      question: 'What is the water supply arrangement?',
      answer: 'Purva Swanlake has a reliable water supply system with dedicated water management infrastructure. We provide 24/7 water supply with backup systems. The water quality is regularly monitored and maintained to ensure safe drinking water for all residents.',
    },
    {
      id: 'power',
      question: 'Is there power backup?',
      answer: 'Yes, we have 100% power backup with generators. Every apartment has 3000W backup capacity, and all common areas including lifts and pumps have uninterrupted electricity supply. This ensures continuous power supply throughout the year.',
    },
    {
      id: 'internet',
      question: 'Is high-speed internet available?',
      answer: 'Yes, Purva Swanlake has integrated high-speed internet connectivity infrastructure throughout the community. Residents can connect with multiple internet service providers and enjoy reliable, high-speed connectivity in their apartments.',
    },
    {
      id: 'bylaws',
      question: 'Where can I find the community by-laws?',
      answer: 'The community by-laws are available on our website under the "Documents" section in the footer. You can download the PDF directly or contact the office at office.psaoa@gmail.com for a physical copy. The by-laws govern all community rules and regulations.',
    },
    {
      id: 'contact',
      question: 'How do I contact the management office?',
      answer: 'You can reach the management office at office.psaoa@gmail.com or call 88389 07864. Office hours are 10:30 AM to 6:00 PM open all days of the week. For emergencies, contact the 24/7 security desk at the main gate.',
    },
    {
      id: 'visitors',
      question: 'What is the visitor policy?',
      answer: 'All visitors must register at the main gate and follow the security protocols. Residents are responsible for their visitors. Overnight visitors require prior intimation to the management office. This ensures the safety and security of all residents.',
    },
    {
      id: 'parking',
      question: 'What are the parking arrangements?',
      answer: 'Each apartment comes with dedicated parking spaces. Additional parking is available in the community parking areas. Parking is managed by the facility management team. Visitors can park in designated visitor parking areas near the main gate.',
    },
  ];

  const toggleExpanded = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Find answers to common questions about Purva Swanlake amenities, facilities, and community policies.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <button
                onClick={() => toggleExpanded(faq.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors duration-200"
              >
                <h3 className="text-left text-lg font-semibold text-slate-900">
                  {faq.question}
                </h3>
                <ChevronDown
                  className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform duration-300 ${
                    expandedId === faq.id ? 'transform rotate-180' : ''
                  }`}
                />
              </button>

              {expandedId === faq.id && (
                <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
                  <p className="text-slate-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <h3 className="text-xl font-semibold text-slate-900 mb-3">
            Didn't find your answer?
          </h3>
          <p className="text-slate-700 mb-4">
            Contact us directly and our team will be happy to help you.
          </p>
          <a
            href="mailto:office.psaoa@gmail.com"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300"
          >
            Contact Us
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
